import React, { useState } from 'react'
import { Image, Modal, Button, Row, Col, Form, Badge } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import {
  Search,
  Star,
  StarFill,
  CheckCircleFill,
  ChevronLeft,
  ChevronRight,
  XLg,
  Cpu,
  Lightning,
  Diagram3,
  ArrowsAngleExpand,
} from 'react-bootstrap-icons'
import styled from 'styled-components'
import { selectSelectedModelId } from '../../store/slices/modelSlice'
import { useGetModelQuery } from '../../services/modelsApi'
import { useAppSelector } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../services/projectsApi'

interface BBoxModel {
  id: string
  name: string
  description: string
  rating: number
  image: string
  metrics: {
    mAP: number
    precision: number
    f1: number
  }
}

interface ClassificationModel {
  id: string
  name: string
  description: string
  rating: number
  image: string
  metrics: {
    mAP: number
  }
}

interface PretrainedModels {
  bounding_box: BBoxModel[]
  classification: ClassificationModel[]
}

// Mock data for pretrained models
const MOCK_PRETRAINED_MODELS: PretrainedModels = {
  bounding_box: [
    {
      id: 'pt-1',
      name: 'AG Vision - Satellite Pro',
      description:
        'High-resolution satellite imagery detection model optimized for small object detection from space',
      rating: 5,
      image: '/DALLE-AI-Model-Logo-1.jpg',
      metrics: {
        mAP: 0.94,
        precision: 0.96,
        f1: 0.95,
      },
    },
    {
      id: 'pt-2',
      name: 'AG Vision - Maritime Vessels',
      description:
        'Specialized in detecting and classifying ships, boats, and maritime infrastructure from satellite imagery',
      rating: 4,
      image: '/DALLE-AI-Model-Logo-1.jpg',
      metrics: {
        mAP: 0.91,
        precision: 0.93,
        f1: 0.92,
      },
    },
    {
      id: 'pt-3',
      name: 'AG Vision - Urban Analytics',
      description:
        'Optimized for urban infrastructure detection - buildings, roads, vehicles from aerial imagery',
      rating: 5,
      image: '/DALLE-AI-Model-Logo-1.jpg',
      metrics: {
        mAP: 0.93,
        precision: 0.94,
        f1: 0.93,
      },
    },
    {
      id: 'pt-4',
      name: 'AG Vision - Agriculture Monitor',
      description:
        'Specialized in crop field analysis, irrigation systems, and agricultural equipment detection from above',
      rating: 4,
      image: '/DALLE-AI-Model-Logo-1.jpg',
      metrics: {
        mAP: 0.89,
        precision: 0.91,
        f1: 0.9,
      },
    },
    {
      id: 'pt-5',
      name: 'AG Vision - Defense & Security',
      description:
        'Advanced model for detecting military assets, security infrastructure, and strategic facilities',
      rating: 5,
      image: '/DALLE-AI-Model-Logo-1.jpg',
      metrics: {
        mAP: 0.95,
        precision: 0.97,
        f1: 0.96,
      },
    },
  ],
  classification: [
    {
      id: 'pt-c1',
      name: 'AG Vision - Crop Disease Classifier',
      description:
        'Advanced model for identifying various crop diseases and health conditions from aerial imagery',
      rating: 5,
      image: '/DALLE-AI-Model-Logo-2.jpg',
      metrics: {
        mAP: 0.96,
      },
    },
    {
      id: 'pt-c2',
      name: 'AG Vision - Land Use Classifier',
      description:
        'Specialized in classifying different types of land use and terrain from satellite imagery',
      rating: 4,
      image: '/DALLE-AI-Model-Logo-2.jpg',
      metrics: {
        mAP: 0.93,
      },
    },
    {
      id: 'pt-c3',
      name: 'AG Vision - Infrastructure Quality',
      description:
        'Classifies infrastructure condition and maintenance needs from aerial photography',
      rating: 5,
      image: '/DALLE-AI-Model-Logo-2.jpg',
      metrics: {
        mAP: 0.94,
      },
    },
  ],
}

interface NewModelModalProps {
  show: boolean
  onClose: () => void
  baseModels: { id: string; name: string; image: string }[]
  filterText: string
  setFilterText: (text: string) => void
  selectedBaseModel: { id: string; name: string } | null
  onBaseModelSelect: (model: { id: string; name: string }) => void
  onGenerateNewModel: () => void
}

interface BaseModelDetails {
  type: 'base'
  architecture: string
  framework: string
  input_size: string
  flops: string
  parameters: string
  features: string[]
  optimizations: string[]
}

interface PretrainedModelDetails {
  type: 'pretrained'
  architecture: string
  framework: string
  input_size: string
  flops: string
  parameters: string
  training_data: string
  specializations: string[]
  fine_tuning: string[]
}

type ModelDetails = BaseModelDetails | PretrainedModelDetails

const ComingSoonBanner = styled.div`
  position: relative;
  padding: 2.5rem 4rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 2.5rem;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 1.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 400px;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
`

const ComingSoonOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(13, 16, 33, 0.5) 0%, rgba(20, 25, 65, 0.5) 100%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='50' cy='50' r='1' fill='rgba(255,255,255,0.15)'/%3E%3C/svg%3E");
    opacity: 0.2;
    animation: twinkle 4s infinite alternate;
  }

  @keyframes twinkle {
    0% {
      opacity: 0.1;
    }
    100% {
      opacity: 0.4;
    }
  }
`

const PretrainedCarousel = styled.div`
  height: 360px;
  padding: 0.5rem 0;
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  opacity: 0.2;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const NewModelModal: React.FC<NewModelModalProps> = ({
  show,
  onClose,
  baseModels,
  filterText,
  setFilterText,
  selectedBaseModel,
  onBaseModelSelect,
  onGenerateNewModel,
}) => {
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const location = useLocation()

  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })

  const [scrollPosition, setScrollPosition] = useState(0)
  const [showDetails, setShowDetails] = useState(false)
  const [selectedModelDetails, setSelectedModelDetails] = useState<any>(null)

  const handleScroll = (direction: 'left' | 'right') => {
    const container = document.querySelector('.pretrained-models-carousel')
    if (container) {
      const scrollAmount = direction === 'left' ? -400 : 400
      const newPosition = scrollPosition + scrollAmount
      container.scrollTo({
        left: newPosition,
        behavior: 'smooth',
      })
      setScrollPosition(newPosition)
    }
  }

  // Mock details for base and pretrained models
  const getModelDetails = (model: any, type: 'base' | 'pretrained'): ModelDetails => {
    if (type === 'base') {
      return {
        type: 'base',
        architecture: 'YOLOv8-Large',
        framework: 'PyTorch 2.0',
        input_size: '1024x1024',
        flops: '117.4 GFLOPS',
        parameters: '43.7M params',

        features: [
          'Multi-scale predictions',
          'Anchor-free detection',
          'Mosaic and MixUp augmentation',
          'Cross mini-batch normalization',
          'Adaptive image scaling',
        ],
        optimizations: [
          'FP16 precision support',
          'ONNX export capability',
          'TensorRT compatibility',
          'Multi-GPU training support',
        ],
      }
    }
    return {
      type: 'pretrained',
      architecture: 'YOLOv8-Large (Fine-tuned)',
      framework: 'PyTorch 2.0',
      input_size: '640x640',
      flops: '117.4 GFLOPS',
      parameters: '43.7M params',
      training_data: 'Custom satellite imagery dataset',
      specializations: [
        'Small object detection optimization',
        'Scale-variant feature extraction',
        'Aerial perspective adaptation',
        'Multi-resolution processing',
      ],
      fine_tuning: [
        'Custom anchor optimization',
        'Domain-specific augmentations',
        'Modified loss function weights',
        'Specialized NMS parameters',
      ],
    }
  }

  const renderBBoxModelCard = (model: BBoxModel, type: 'base' | 'pretrained') => {
    const isSelected =
      type === 'base' ? selectedBaseModel?.id === model.id : selectedModel?.id === model.id

    // Base model default metrics
    const baseModelMetrics = {
      mAP: 0.85,
      precision: 0.87,
      f1: 0.86,
    }

    return (
      <div
        key={model.id}
        className={`model-catalog-card ${isSelected ? 'selected' : ''}`}
        onClick={() => {
          if (type === 'base') {
            onBaseModelSelect(model)
          } else {
            // No need to set selected model
          }
          setSelectedModelDetails({ ...model, type })
          // setShowDetails(true)
        }}
      >
        {isSelected && type === 'base' && (
          <div className="selected-for-launch">
            <CheckCircleFill />
            <span>Selected for Launch</span>
          </div>
        )}
        <div className="model-catalog-image">
          <Image src={model.image} alt={model.name} className="model-thumbnail" />
          <div className="model-rating">
            {[...Array(5)].map((_, index) =>
              index < (model.rating || 4) ? <StarFill key={index} /> : <Star key={index} />
            )}
          </div>
        </div>
        <div className="model-info">
          <h5>{model.name}</h5>
          <p className="model-description">
            {type === 'base'
              ? 'Base AG Vision model optimized for general object detection'
              : model.description || 'Pre-trained model fine-tuned on custom dataset'}
          </p>
          <div className="model-metrics">
            <Badge bg="primary">
              mAP: {(type === 'pretrained' ? model.metrics.mAP : baseModelMetrics.mAP).toFixed(2)}
            </Badge>
            <Badge bg="info">
              Precision:{' '}
              {(type === 'pretrained'
                ? model.metrics.precision
                : baseModelMetrics.precision
              ).toFixed(2)}
            </Badge>
            <Badge bg="success">
              F1: {(type === 'pretrained' ? model.metrics.f1 : baseModelMetrics.f1).toFixed(2)}
            </Badge>
          </div>
        </div>
      </div>
    )
  }

  const renderClassificationModelCard = (
    model: ClassificationModel,
    type: 'base' | 'pretrained'
  ) => {
    const isSelected =
      type === 'base' ? selectedBaseModel?.id === model.id : selectedModel?.id === model.id

    // Base model default metrics for classification
    const baseModelMetrics = {
      mAP: 0.88,
    }

    return (
      <div
        key={model.id}
        className={`model-catalog-card ${isSelected ? 'selected' : ''}`}
        onClick={() => {
          if (type === 'base') {
            onBaseModelSelect(model)
          } else {
            // No need to set selected model
          }
          setSelectedModelDetails({ ...model, type })
        }}
      >
        {isSelected && type === 'base' && (
          <div className="selected-for-launch">
            <CheckCircleFill />
            <span>Selected for Launch</span>
          </div>
        )}
        <div className="model-catalog-image">
          <Image src={model.image} alt={model.name} className="model-thumbnail" />
          <div className="model-rating">
            {[...Array(5)].map((_, index) =>
              index < (model.rating || 4) ? <StarFill key={index} /> : <Star key={index} />
            )}
          </div>
        </div>
        <div className="model-info">
          <h5>{model.name}</h5>
          <p className="model-description">
            {type === 'base'
              ? 'Base AG Vision model optimized for image classification'
              : model.description || 'Pre-trained classification model'}
          </p>
          <div className="model-metrics">
            <Badge bg="primary">
              mAP: {(type === 'pretrained' ? model.metrics.mAP : baseModelMetrics.mAP).toFixed(2)}
            </Badge>
          </div>
        </div>
      </div>
    )
  }

  const filteredPretrainedModels =
    project?.annotation_type === 'classification'
      ? MOCK_PRETRAINED_MODELS.classification.filter((model: ClassificationModel) =>
          model.name.toLowerCase().includes(filterText.toLowerCase())
        )
      : MOCK_PRETRAINED_MODELS.bounding_box.filter((model: BBoxModel) =>
          model.name.toLowerCase().includes(filterText.toLowerCase())
        )

  return (
    <Modal show={show} onHide={onClose} size="xl" className="model-catalog-modal">
      <Modal.Header className="bg-dark text-white">
        <Modal.Title>Model Catalog</Modal.Title>
        {selectedBaseModel && (
          <div className="selected-model-indicator">
            <span>Selected: </span>
            <strong>{selectedBaseModel.name}</strong>
          </div>
        )}
        <button className="modal-close-btn" onClick={onClose}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </Modal.Header>
      <Modal.Body className="bg-dark text-white">
        <Row className="h-100">
          {showDetails && (
            <Col md={3} className={`details-panel-col ${showDetails ? 'show' : ''}`}>
              <div className="model-details-panel">
                <button className="close-details-btn" onClick={() => setShowDetails(false)}>
                  <XLg size={20} />
                </button>
                {selectedModelDetails && (
                  <div className="details-content">
                    <h3>{selectedModelDetails.name}</h3>
                    <p className="details-description">{selectedModelDetails.description}</p>

                    <div className="details-section">
                      <h4>Technical Specifications</h4>
                      <div className="spec-grid">
                        <div className="spec-item">
                          <Cpu />
                          <div>
                            <strong>Architecture</strong>
                            <span>
                              {
                                getModelDetails(selectedModelDetails, selectedModelDetails.type)
                                  ?.architecture
                              }
                            </span>
                          </div>
                        </div>
                        <div className="spec-item">
                          <Lightning />
                          <div>
                            <strong>Framework</strong>
                            <span>
                              {
                                getModelDetails(selectedModelDetails, selectedModelDetails.type)
                                  ?.framework
                              }
                            </span>
                          </div>
                        </div>
                        <div className="spec-item">
                          <ArrowsAngleExpand />
                          <div>
                            <strong>Input Size</strong>
                            <span>
                              {
                                getModelDetails(selectedModelDetails, selectedModelDetails.type)
                                  ?.input_size
                              }
                            </span>
                          </div>
                        </div>
                        <div className="spec-item">
                          <Diagram3 />
                          <div>
                            <strong>Model Size</strong>
                            <span>
                              {
                                getModelDetails(selectedModelDetails, selectedModelDetails.type)
                                  ?.parameters
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {selectedModelDetails.type === 'base' ? (
                      <>
                        <div className="details-section">
                          <h4>Key Features</h4>
                          <ul className="feature-list">
                            {(() => {
                              const details = getModelDetails(selectedModelDetails, 'base')
                              return (
                                details.type === 'base' &&
                                details.features.map((feature: string, index: number) => (
                                  <li key={index}>{feature}</li>
                                ))
                              )
                            })()}
                          </ul>
                        </div>
                        <div className="details-section">
                          <h4>Optimizations</h4>
                          <ul className="feature-list">
                            {(() => {
                              const details = getModelDetails(selectedModelDetails, 'base')
                              return (
                                details.type === 'base' &&
                                details.optimizations.map((opt: string, index: number) => (
                                  <li key={index}>{opt}</li>
                                ))
                              )
                            })()}
                          </ul>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="details-section">
                          <h4>Specializations</h4>
                          <ul className="feature-list">
                            {(() => {
                              const details = getModelDetails(selectedModelDetails, 'pretrained')
                              return (
                                details.type === 'pretrained' &&
                                details.specializations.map((spec: string, index: number) => (
                                  <li key={index}>{spec}</li>
                                ))
                              )
                            })()}
                          </ul>
                        </div>
                        <div className="details-section">
                          <h4>Fine-tuning Details</h4>
                          <ul className="feature-list">
                            {(() => {
                              const details = getModelDetails(selectedModelDetails, 'pretrained')
                              return (
                                details.type === 'pretrained' &&
                                details.fine_tuning.map((detail: string, index: number) => (
                                  <li key={index}>{detail}</li>
                                ))
                              )
                            })()}
                          </ul>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Col>
          )}

          <Col
            md={showDetails ? 9 : 12}
            className={`main-content-col ${showDetails ? 'shifted' : ''}`}
          >
            <div className="model-catalog-search">
              <Form.Group>
                <div className="search-input-wrapper">
                  <Search size={20} />
                  <Form.Control
                    type="text"
                    placeholder="Search models..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="search-input"
                  />
                </div>
              </Form.Group>
            </div>

            <div className="model-sections">
              <section className="base-models-section">
                <h4>Base Models</h4>
                <div className="models-grid">
                  {baseModels
                    ?.filter((model) => model.name.toLowerCase().includes(filterText.toLowerCase()))
                    .map((model) => (
                      <div key={model.id} className="model-card-wrapper">
                        {project?.annotation_type === 'classification'
                          ? renderClassificationModelCard(model as ClassificationModel, 'base')
                          : renderBBoxModelCard(model as BBoxModel, 'base')}
                      </div>
                    ))}
                </div>
              </section>

              <section className="pretrained-models-section">
                <h4>Pre-trained Models (Coming Soon)</h4>
                <Row>
                  <Col>
                    <div className="carousel-container" style={{ position: 'relative' }}>
                      <ComingSoonOverlay>
                        <ComingSoonBanner>Coming Soon</ComingSoonBanner>
                      </ComingSoonOverlay>
                      <button
                        className="carousel-nav-btn prev"
                        onClick={() => handleScroll('left')}
                        disabled
                      >
                        <ChevronLeft size={24} />
                      </button>
                      <PretrainedCarousel>
                        {filteredPretrainedModels.map((model) =>
                          project?.annotation_type === 'classification'
                            ? renderClassificationModelCard(
                                model as ClassificationModel,
                                'pretrained'
                              )
                            : renderBBoxModelCard(model as BBoxModel, 'pretrained')
                        )}
                      </PretrainedCarousel>
                      <button
                        className="carousel-nav-btn next"
                        onClick={() => handleScroll('right')}
                        disabled
                      >
                        <ChevronRight size={24} />
                      </button>
                    </div>
                  </Col>
                </Row>
              </section>
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="bg-dark text-white">
        <Button
          variant="primary"
          onClick={onGenerateNewModel}
          disabled={!selectedBaseModel}
          className="launch-model-btn"
        >
          Launch Model
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default NewModelModal
